import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { PlusCircle, Eye, Trash2, UserPlus, Building2 } from 'lucide-react';
import { getOnboardings, deleteOnboarding } from '../utils/api';
import { ShareLinkButton } from '../components/ShareLinkButton';

const InvestorOnboarding = () => {
  const navigate = useNavigate();
  const [onboardings, setOnboardings] = useState([]);

  useEffect(() => {
    loadOnboardings();
  }, []);

  const loadOnboardings = async () => {
    try {
      setOnboardings(await getOnboardings());
    } catch (err) {
      console.error('Error loading onboardings:', err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this onboarding record?')) {
      try {
        await deleteOnboarding(id);
        loadOnboardings();
      } catch (err) {
        alert('Failed to delete: ' + err.message);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Investor Onboarding</h1>
            <p className="text-slate-600">Complete KYC and onboarding documentation for new investors</p>
          </div>
          <div className="flex items-center gap-3">
            <ShareLinkButton kind="onboarding" label="Send Self-Fill Link" className="border-green-500 text-green-700 hover:bg-green-50" />
            <Button
              onClick={() => navigate('/admin/onboarding/create-entity')}
              variant="outline"
              className="border-blue-500 text-blue-700 hover:bg-blue-50"
              data-testid="new-non-individual-btn"
            >
              <Building2 className="mr-2" size={20} />
              Non-Individual
            </Button>
            <Button
              onClick={() => navigate('/admin/onboarding/create')}
              className="bg-green-600 hover:bg-green-700"
            >
              <PlusCircle className="mr-2" size={20} />
              New Onboarding
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus size={24} />
              All Onboarding Records
            </CardTitle>
          </CardHeader>
          <CardContent>
            {onboardings.length === 0 ? (
              <div className="text-center py-12">
                <UserPlus size={64} className="mx-auto text-slate-300 mb-4" />
                <p className="text-slate-600 text-lg mb-4">No onboarding records yet</p>
                <Button
                  onClick={() => navigate('/admin/onboarding/create')}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <PlusCircle className="mr-2" size={20} />
                  Create First Record
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Investor Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        PAN
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Mobile
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Onboarding Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    {onboardings.map((record) => {
                      const isEntity = record.client_type === 'non_individual';
                      const displayName = isEntity ? record.entity_name : record.name;
                      const displayPan = isEntity ? record.entity_pan_number : record.pan;
                      const displayMobile = isEntity ? record.phone : record.mobile;
                      return (
                      <tr key={record.id} className="hover:bg-slate-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-slate-900">{displayName || '—'}</div>
                          {isEntity && <span className="inline-block mt-0.5 text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">{record.entity_type || 'Non-Individual'}</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                          {displayPan}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                          {record.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                          {displayMobile}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                          {new Date(record.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/admin/onboarding/view/${record.id}`)}
                            >
                              <Eye size={16} className="mr-1" />
                              View
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(record.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="mt-6">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/dashboard')}
          >
            ← Back to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InvestorOnboarding;
